/**
 * «Оплата» — экран родителя.
 *
 * Один вопрос, на который экран отвечает: сколько и когда платить. Поэтому
 * сверху сумма текущего периода, дальше детализация, и ровно одна главная
 * кнопка, меняющаяся по состоянию: «Отправить няне» → «Перевести» → ничего.
 */

import { useState } from 'react'

import { useCurrency, useFamily, useTimeZone } from '@/app/session'
import { downloadTimesheetCsv } from '@/features/pay/export'
import { PaymentSheet } from '@/features/pay/PaymentSheet'
import { TimesheetDetails } from '@/features/pay/TimesheetDetails'
import { TimesheetSummary } from '@/features/pay/TimesheetSummary'
import {
  ADJUSTMENT_LABELS,
  STATUS_LABELS,
  useAddAdjustment,
  useAddAdvance,
  useCurrentTimesheet,
  useResolveDispute,
  useTimesheet,
  useSendTimesheet,
  useTimesheetHistory,
  useVoidAdjustment,
  type Adjustment,
  type AdjustmentType,
} from '@/shared/api/pay'
import { formatDuration } from '@/shared/format'
import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Screen } from '@/shared/ui/Screen'
import { Icon } from '@/shared/ui/Icon'
import { ConfirmSheet, Sheet } from '@/shared/ui/Sheet'
import { Field, MoneyInput, TextInput } from '@/shared/ui/fields'
import { Card, Chip, Money, Row, SectionTitle } from '@/shared/ui/primitives'
import { ErrorState, SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

const ADJUSTMENT_TYPES: AdjustmentType[] = ['bonus', 'advance', 'cash_given', 'correction']

export function Pay() {
  const family = useFamily()
  const timeZone = useTimeZone()
  const currency = useCurrency()
  const familyId = family?.id ?? 0

  const [payOpen, setPayOpen] = useState(false)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)
  const [voiding, setVoiding] = useState<Adjustment | null>(null)
  const [resolveOpen, setResolveOpen] = useState(false)
  const [historyId, setHistoryId] = useState<number | null>(null)

  const current = useCurrentTimesheet(familyId)
  const history = useTimesheetHistory(familyId)

  const send = useSendTimesheet(familyId, {
    onSuccess: () => {
      haptic('success')
      setSendOpen(false)
      toast.success('Расчёт отправлен няне')
    },
    onError: (error) => toast.error(error.message),
  })
  const resolve = useResolveDispute(familyId, {
    onSuccess: () => {
      setResolveOpen(false)
      toast.success('Вопрос закрыт')
    },
    onError: (error) => toast.error(error.message),
  })

  if (!family) return null
  if (current.isLoading) {
    return (
      <Screen title="Оплата">
        <SkeletonCard lines={3} />
        <SkeletonCard lines={2} />
      </Screen>
    )
  }
  if (current.error) {
    return (
      <Screen title="Оплата">
        <ErrorState error={current.error} onRetry={() => void current.refetch()} />
      </Screen>
    )
  }

  const timesheet = current.data!
  const closed = (history.data?.items ?? []).filter((item) => item.id !== timesheet.id)

  return (
    <Screen
      title="Оплата"
      right={
        <Button size="sm" variant="secondary" onClick={() => setAdjustOpen(true)}>
          + Строка
        </Button>
      }
    >
      <TimesheetSummary timesheet={timesheet} />

      {timesheet.status === 'disputed' && timesheet.dispute_note && (
        <Card className="bg-danger-soft">
          <p className="text-danger text-body font-semibold">Няня задала вопрос</p>
          <p className="mt-1 text-note leading-snug">{timesheet.dispute_note}</p>
          <Button className="mt-3" block variant="secondary" onClick={() => setResolveOpen(true)}>
            Разобрались
          </Button>
        </Card>
      )}

      {timesheet.status === 'draft' && (
        <Button
          block
          size="lg"
          disabled={Number(timesheet.amount_final) === 0 && timesheet.shifts.length === 0}
          onClick={() => setSendOpen(true)}
        >
          Отправить няне
        </Button>
      )}

      {(timesheet.status === 'sent' || timesheet.status === 'disputed') && (
        <Button block size="lg" onClick={() => setPayOpen(true)}>
          Перевести {timesheet.payee?.phone ? `на ${timesheet.payee.phone}` : ''}
        </Button>
      )}

      {timesheet.status === 'paid_declared' && (
        <Card className="text-center">
          <p className="text-body font-medium">Перевод отмечен</p>
          <p className="text-text-muted mt-1 text-note leading-snug">
            Ждём подтверждения от {timesheet.nanny_name}. Как только она отметит получение,
            период закроется.
          </p>
        </Card>
      )}

      {timesheet.status === 'confirmed' && (
        <Card className="bg-success-soft text-center">
          <p className="text-success flex items-center gap-1.5 text-body font-semibold">
            <Icon name="state-success" size={18} />
            Период закрыт
          </p>
          <p className="text-text-muted mt-1 text-note">
            {timesheet.nanny_name} подтвердила получение
          </p>
        </Card>
      )}

      <TimesheetDetails
        timesheet={timesheet}
        timeZone={timeZone}
        onVoidAdjustment={timesheet.status === 'draft' ? setVoiding : undefined}
      />

      {closed.length > 0 && (
        <div>
          <SectionTitle
            action={
              history.data?.total_paid ? (
                <span className="text-text-muted text-caption">
                  всего {formatDuration(history.data.total_minutes)}
                </span>
              ) : undefined
            }
          >
            История
          </SectionTitle>
          <Card padded={false} className="px-4">
            {closed.map((item, index) => (
              <div key={item.id}>
                {index > 0 && <div className="bg-line h-px" />}
                <Row
                  title={item.period_title}
                  subtitle={formatDuration(item.minutes_total)}
                  right={
                    <span className="flex items-center gap-2">
                      {item.status !== 'confirmed' && (
                        <Chip tone="warm">{STATUS_LABELS[item.status]}</Chip>
                      )}
                      <Money value={item.amount_final} currency={item.currency} size="sm" />
                    </span>
                  }
                  onClick={() => setHistoryId(item.id)}
                />
              </div>
            ))}
          </Card>
        </div>
      )}

      <ConfirmSheet
        open={sendOpen}
        title="Отправить расчёт няне?"
        description="После отправки суммы периода зафиксируются: смены и расходы больше не редактируются, исправить можно будет только отдельной корректировкой."
        confirmLabel="Отправить"
        loading={send.isPending}
        onConfirm={() => send.mutate(timesheet.id)}
        onClose={() => setSendOpen(false)}
      />

      <PaymentSheet
        open={payOpen}
        timesheet={timesheet}
        familyId={familyId}
        onClose={() => setPayOpen(false)}
      />

      <AdjustmentSheet
        open={adjustOpen}
        timesheet={timesheet}
        familyId={familyId}
        currency={currency}
        onClose={() => setAdjustOpen(false)}
      />

      <VoidSheet
        adjustment={voiding}
        familyId={familyId}
        onClose={() => setVoiding(null)}
      />

      <HistorySheet
        familyId={familyId}
        timesheetId={historyId}
        timeZone={timeZone}
        onClose={() => setHistoryId(null)}
      />

      <Sheet
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        title="Закрыть вопрос"
      >
        <ResolveForm
          onSubmit={(resolution) => resolve.mutate({ id: timesheet.id, resolution })}
          pending={resolve.isPending}
        />
      </Sheet>
    </Screen>
  )
}

/** Премия, аванс, выданные наличные, исправление. */
function AdjustmentSheet({
  open,
  timesheet,
  familyId,
  currency,
  onClose,
}: {
  open: boolean
  timesheet: { id: number; status: string }
  familyId: number
  currency: string
  onClose: () => void
}) {
  const [type, setType] = useState<AdjustmentType>('bonus')
  const [title, setTitle] = useState('')
  const [amount, setAmount] = useState('')

  const done = () => {
    haptic('success')
    setTitle('')
    setAmount('')
    onClose()
  }

  const add = useAddAdjustment(familyId, {
    onSuccess: () => {
      toast.success('Строка добавлена')
      done()
    },
    onError: (error) => toast.error(error.message),
  })
  const advance = useAddAdvance(familyId, {
    onSuccess: () => {
      toast.success('Аванс учтён в текущем расчёте')
      done()
    },
    onError: (error) => toast.error(error.message),
  })

  const locked = timesheet.status !== 'draft'
  const pending = add.isPending || advance.isPending
  const canSubmit = Boolean(amount) && Number(amount) > 0 && !pending

  const submit = () => {
    const label = title.trim() || ADJUSTMENT_LABELS[type]
    // Аванс в закрытом периоде всё равно осмыслен: он уйдёт в текущий.
    if (type === 'advance' && locked) {
      advance.mutate({ amount, title: label })
      return
    }
    add.mutate({ timesheetId: timesheet.id, type, title: label, amount })
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Строка расчёта"
      footer={
        <Button block size="lg" loading={pending} disabled={!canSubmit} onClick={submit}>
          Добавить
        </Button>
      }
    >
      {locked && type !== 'advance' && (
        <p className="bg-warm-soft text-warm-ink mb-3 rounded-[var(--radius-button)] px-4 py-3 text-note leading-snug">
          Расчёт уже отправлен. Правки в него не вносятся — исправление нужно оформить
          отдельной строкой в следующем периоде.
        </p>
      )}

      <div className="grid grid-cols-2 gap-2">
        {ADJUSTMENT_TYPES.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => {
              haptic('select')
              setType(item)
            }}
            className={
              'press min-h-[44px] rounded-[var(--radius-button)] px-3 text-note ' +
              (type === item
                ? 'bg-accent-soft text-accent'
                : 'bg-surface-muted text-text-muted')
            }
          >
            {ADJUSTMENT_LABELS[item]}
          </button>
        ))}
      </div>

      <p className="text-text-muted mt-2 px-1 text-caption leading-snug">
        {type === 'bonus' && 'Прибавится к выплате.'}
        {type === 'advance' && 'Деньги уже выданы — вычтется из выплаты.'}
        {type === 'cash_given' && 'Выдано наличными на расходы — вычтется из выплаты.'}
        {type === 'correction' && 'Исправление ошибки прошлого периода.'}
      </p>

      <div className="mt-4">
        <Field label="Сумма">
          <MoneyInput
            value={amount}
            onValueChange={setAmount}
            currency={currency === 'RUB' ? '₽' : currency}
          />
        </Field>
      </div>

      <div className="mt-4">
        <Field label="Подпись" hint="Няня увидит эту строку в расчёте.">
          <TextInput
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={ADJUSTMENT_LABELS[type]}
            maxLength={64}
          />
        </Field>
      </div>
    </Sheet>
  )
}

function VoidSheet({
  adjustment,
  familyId,
  onClose,
}: {
  adjustment: Adjustment | null
  familyId: number
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const voidIt = useVoidAdjustment(familyId, {
    onSuccess: () => {
      toast.show('Строка отменена')
      setReason('')
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })

  return (
    <Sheet
      open={adjustment !== null}
      onClose={() => {
        setReason('')
        onClose()
      }}
      title={adjustment ? `Отменить: ${adjustment.title}` : ''}
      footer={
        <Button
          block
          size="lg"
          variant="danger"
          loading={voidIt.isPending}
          disabled={!reason.trim()}
          onClick={() => adjustment && voidIt.mutate({ id: adjustment.id, reason: reason.trim() })}
        >
          Отменить строку
        </Button>
      }
    >
      <Field
        label="Причина"
        hint="Строка не исчезнет: она останется в расчёте зачёркнутой, с этой причиной. Так видно, что менялось."
      >
        <TextInput
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Ошиблись суммой"
          maxLength={200}
          autoFocus
        />
      </Field>
    </Sheet>
  )
}

function ResolveForm({
  onSubmit,
  pending,
}: {
  onSubmit: (resolution: string) => void
  pending: boolean
}) {
  const [text, setText] = useState('')
  return (
    <>
      <Field
        label="Что решили"
        hint="Няня увидит это в истории расчёта — вопрос закроется с ответом, а не молча."
      >
        <TextInput
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Проверили, добавили корректировкой"
          maxLength={200}
          autoFocus
        />
      </Field>
      <Button
        className="mt-4"
        block
        size="lg"
        loading={pending}
        disabled={!text.trim()}
        onClick={() => onSubmit(text.trim())}
      >
        Закрыть вопрос
      </Button>
    </>
  )
}


/** Закрытый период: детализация и выгрузка. */
function HistorySheet({
  familyId,
  timesheetId,
  timeZone,
  onClose,
}: {
  familyId: number
  timesheetId: number | null
  timeZone: string
  onClose: () => void
}) {
  const [exporting, setExporting] = useState(false)
  const query = useTimesheet(familyId, timesheetId)
  const timesheet = query.data

  return (
    <Sheet
      open={timesheetId !== null}
      onClose={onClose}
      title={timesheet ? timesheet.period_title : 'Расчёт'}
      footer={
        timesheet ? (
          <Button
            block
            size="lg"
            variant="secondary"
            loading={exporting}
            onClick={async () => {
              setExporting(true)
              try {
                await downloadTimesheetCsv(
                  timesheet.id,
                  `yanyana_${timesheet.period_start}_${timesheet.period_end}.csv`,
                )
              } catch {
                toast.error('Ваш Telegram не даёт сохранить файл — откройте приложение в браузере')
              } finally {
                setExporting(false)
              }
            }}
          >
            Выгрузить в CSV
          </Button>
        ) : undefined
      }
    >
      {query.isLoading && <SkeletonCard lines={3} />}
      {timesheet && (
        <div className="space-y-3">
          <TimesheetSummary timesheet={timesheet} />
          <TimesheetDetails timesheet={timesheet} timeZone={timeZone} />
        </div>
      )}
    </Sheet>
  )
}
